import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useForm, type FieldErrors, type FieldPath } from 'react-hook-form'
import { CheckCircle2, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import { Stepper, type StepperStep } from '@/components/ui/stepper'
import { useAuth } from '@/hooks/useAuth'
import { mailingListService } from '@/features/mailing-list'
import { applicationsService } from '../services/applications.service'
import { DeadlineBanner } from '../components/DeadlineBanner'
import { ApplicantInfoSection } from '../components/ApplicantInfoSection'
import { DynamicFormRenderer, type DynamicFormValues } from '../components/DynamicFormRenderer'
import { ApplicationPaymentStep } from '../components/ApplicationPaymentStep'
import { useApplicationForm } from '../hooks/useApplicationForm'
import {
  createApplicationPaymentIntentFn,
  type ApplicationUploadReceipt,
} from '@/lib/firebase/functions'
import {
  createUserFacingError,
  formatApplicationSubmissionError,
  withErrorCode,
} from '@/lib/errors'
import type { ApplicationField, ApplicationSection, FieldAnswer } from '@/types'

export function ApplicationDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const { user } = useAuth()
  const { application, form, loading, error, submitting, submitted, submit } = useApplicationForm(
    slug ?? ''
  )

  const [submitError, setSubmitError] = useState<string | null>(null)
  const [fileUploadActivity, setFileUploadActivity] = useState<Record<string, boolean>>({})
  const [submittedEmail, setSubmittedEmail] = useState('')
  const fileUploadsRef = useRef<Record<string, ApplicationUploadReceipt[]>>({})
  const [paymentStep, setPaymentStep] = useState<{
    clientSecret: string
    amountCents: number
    currency: string
  } | null>(null)
  const [initializingPayment, setInitializingPayment] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [activeStep, setActiveStep] = useState(0)
  const [stepValidation, setStepValidation] = useState<Record<number, 'valid' | 'invalid'>>({})
  const stepContentRef = useRef<HTMLDivElement>(null)
  const horizontalStepperRef = useRef<HTMLDivElement>(null)
  const previousStepRef = useRef(0)

  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    trigger,
    formState: { errors },
  } = useForm<DynamicFormValues>({
    defaultValues: {
      applicantInfo: { firstName: '', lastName: '', email: '' },
      answers: {},
      files: {},
    },
  })

  // Pre-fill applicant info from Firebase Auth
  useEffect(() => {
    if (!user) return
    const displayName = user.displayName ?? ''
    const spaceIdx = displayName.indexOf(' ')
    const firstName = spaceIdx > 0 ? displayName.slice(0, spaceIdx) : displayName
    const lastName = spaceIdx > 0 ? displayName.slice(spaceIdx + 1) : ''
    reset({
      applicantInfo: { firstName, lastName, email: user.email ?? '' },
      answers: {},
      files: {},
    })
  }, [user, reset])

  useEffect(() => {
    setActiveStep(0)
    setStepValidation({})
    setTermsAccepted(false)
    setSubmitError(null)
    setPaymentStep(null)
    setFileUploadActivity({})
    fileUploadsRef.current = {}
    previousStepRef.current = 0
  }, [application?.id])

  useEffect(() => {
    if (previousStepRef.current !== activeStep) {
      stepContentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      previousStepRef.current = activeStep
    }

    const activeItem =
      horizontalStepperRef.current?.querySelector<HTMLElement>('[data-active="true"]')
    activeItem?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
  }, [activeStep])

  const sortedSections = useMemo(
    () => [...((form?.sections ?? []) as ApplicationSection[])].sort((a, b) => a.order - b.order),
    [form]
  )

  const paymentField = useMemo<ApplicationField | null>(() => {
    if (!form) return null
    for (const section of form.sections as ApplicationSection[]) {
      for (const field of section.fields) {
        if (field.type === 'payment') return field
      }
    }
    return null
  }, [form])

  const contentStepCount = sortedSections.length + 1
  const totalStepCount = contentStepCount + (paymentField ? 1 : 0)
  const uploadsInProgress = Object.values(fileUploadActivity).some(Boolean)

  const stepperSteps = useMemo<StepperStep[]>(() => {
    function statusDescription(index: number, description: string) {
      if (stepValidation[index] === 'valid') return `${description} · Complete`
      if (stepValidation[index] === 'invalid') return `${description} · Needs review`
      return description
    }

    const steps: StepperStep[] = [
      {
        label: 'Your Information',
        description: statusDescription(0, 'Contact details'),
        status:
          stepValidation[0] === 'valid'
            ? 'complete'
            : stepValidation[0] === 'invalid'
              ? 'error'
              : undefined,
      },
      ...sortedSections.map((section, sectionIndex) => {
        const stepIndex = sectionIndex + 1
        const fieldCount = section.fields.filter((field) => field.type !== 'notice').length
        return {
          label: section.title,
          description: statusDescription(
            stepIndex,
            `${fieldCount} ${fieldCount === 1 ? 'question' : 'questions'}`
          ),
          status:
            stepValidation[stepIndex] === 'valid'
              ? ('complete' as const)
              : stepValidation[stepIndex] === 'invalid'
                ? ('error' as const)
                : undefined,
        }
      }),
    ]

    if (paymentField) {
      steps.push({ label: 'Payment', description: 'Complete submission' })
    }

    return steps.map((step, index) => ({
      ...step,
      disabled: Boolean(paymentStep) || (index === contentStepCount && !paymentStep),
    }))
  }, [contentStepCount, paymentField, paymentStep, sortedSections, stepValidation])

  async function fireMailingListOptIn(values: DynamicFormValues) {
    if (!form) return
    const hasOptIn = (form.sections as ApplicationSection[]).some((s) =>
      s.fields.some((f) => f.type === 'mailing_list' && values.answers[f.id] === true)
    )
    if (hasOptIn) {
      void mailingListService.subscribeOptIn({
        email: values.applicantInfo.email,
        firstName: values.applicantInfo.firstName,
        lastName: values.applicantInfo.lastName,
        source: 'application',
        segments: ['general'],
      })
    }
  }

  async function onSubmit(values: DynamicFormValues) {
    if (!application || !form) return

    if (uploadsInProgress) {
      setSubmitError(
        withErrorCode('Please wait for your file upload to finish.', 'applications/validation')
      )
      return
    }

    if (!termsAccepted) {
      setSubmitError(
        withErrorCode(
          'Please agree to the Terms of Service before submitting.',
          'applications/validation'
        )
      )
      return
    }

    // Check application is still open (client-side guard)
    const deadline: Date | null = application.deadline
      ? ((application.deadline as unknown as { toDate(): Date }).toDate?.() ??
        new Date(application.deadline as unknown as string))
      : null

    if (application.status === 'draft') {
      setSubmitError(
        withErrorCode('This application is not yet open for submissions.', 'app/validation')
      )
      return
    }

    if (application.status === 'closed' || (deadline && deadline < new Date())) {
      setSubmitError(
        withErrorCode('This application is no longer accepting submissions.', 'app/validation')
      )
      return
    }

    setSubmitError(null)

    // Build answers record
    const answers: Record<string, FieldAnswer> = {}
    for (const section of form.sections as ApplicationSection[]) {
      for (const field of section.fields) {
        const raw = values.answers[field.id]
        if (raw !== undefined && raw !== null) {
          answers[field.id] = {
            label: field.label,
            ...(field.fieldLibraryId ? { fieldLibraryId: field.fieldLibraryId } : {}),
            value: raw,
          }
        }
      }
    }

    // Paid path: create PaymentIntent, defer final submit-state toggle until Stripe confirms.
    if (paymentField) {
      const tierSelection = values.answers[paymentField.id]
      if ((paymentField.paymentTiers?.length ?? 0) > 0 && typeof tierSelection !== 'string') {
        setSubmitError(withErrorCode('Please select a payment option.', 'app/validation'))
        return
      }
      setInitializingPayment(true)
      try {
        // Strip the payment answer — the server derives the amount from the form definition.
        const { [paymentField.id]: _omit, ...answersWithoutPayment } = answers
        void _omit
        const result = await createApplicationPaymentIntentFn({
          applicationId: application.id,
          applicantInfo: values.applicantInfo,
          answers: answersWithoutPayment,
          fileUploads: fileUploadsRef.current,
          tierId: typeof tierSelection === 'string' ? tierSelection : undefined,
          termsAccepted: true,
        })
        setSubmittedEmail(values.applicantInfo.email)
        setPaymentStep({
          clientSecret: result.data.clientSecret,
          amountCents: result.data.amountCents,
          currency: result.data.currency,
        })
        setActiveStep(totalStepCount - 1)
        void fireMailingListOptIn(values)
      } catch (err) {
        setSubmitError(
          formatApplicationSubmissionError(
            err,
            'We could not initialize payment. Please try again.'
          )
        )
      } finally {
        setInitializingPayment(false)
      }
      return
    }

    // Free path (unchanged)
    try {
      setSubmittedEmail(values.applicantInfo.email)
      await submit({
        applicantInfo: values.applicantInfo,
        answers,
        fileUploads: fileUploadsRef.current,
        termsAccepted: true,
      })
      void fireMailingListOptIn(values)
    } catch (err) {
      setSubmitError(formatApplicationSubmissionError(err))
    }
  }

  const [paymentConfirmed, setPaymentConfirmed] = useState(false)

  async function handleFileUpload(
    fieldId: string,
    file: File,
    onProgress: (pct: number) => void
  ): Promise<string> {
    if (!application || !form) {
      throw createUserFacingError('This application could not be loaded.')
    }
    const receipt = await applicationsService.uploadFile(
      application.id,
      form.id,
      fieldId,
      file,
      onProgress
    )
    fileUploadsRef.current[fieldId] = [receipt]
    return receipt.url
  }

  function handleFileRemove(fieldId: string) {
    delete fileUploadsRef.current[fieldId]
  }

  function handleFileStateChange(fieldId: string, uploading: boolean) {
    setFileUploadActivity((current) => ({ ...current, [fieldId]: uploading }))
  }

  async function validateStep(index: number): Promise<boolean> {
    if (index === 0) {
      return trigger(['applicantInfo.firstName', 'applicantInfo.lastName', 'applicantInfo.email'])
    }

    const section = sortedSections[index - 1]
    if (!section) return true
    const fields = section.fields
      .filter((field) => field.type !== 'notice')
      .map(
        (field) =>
          (field.type === 'file_upload'
            ? `files.${field.id}`
            : `answers.${field.id}`) as FieldPath<DynamicFormValues>
      )
    return fields.length === 0 ? true : trigger(fields)
  }

  async function validateAndRecordStep(index: number): Promise<boolean> {
    if (index >= contentStepCount) return true
    const isValid = await validateStep(index)
    setStepValidation((current) => ({
      ...current,
      [index]: isValid ? 'valid' : 'invalid',
    }))
    return isValid
  }

  async function navigateToStep(index: number) {
    if (index === activeStep || paymentStep || index >= contentStepCount) return
    await validateAndRecordStep(activeStep)
    setStepValidation((current) => {
      const next = { ...current }
      if (index > activeStep) {
        for (let skipped = activeStep + 1; skipped < index; skipped += 1) {
          next[skipped] ??= 'invalid'
        }
      }
      return next
    })
    setActiveStep(index)
  }

  async function goToNextStep() {
    await validateAndRecordStep(activeStep)
    const nextStep = Math.min(activeStep + 1, contentStepCount - 1)
    setActiveStep(nextStep)
  }

  function handleInvalid(validationErrors: FieldErrors<DynamicFormValues>) {
    let invalidStep = 0
    if (!validationErrors.applicantInfo) {
      const invalidAnswers = validationErrors.answers as Record<string, unknown> | undefined
      const invalidFiles = validationErrors.files as Record<string, unknown> | undefined
      const sectionIndex = sortedSections.findIndex((section) =>
        section.fields.some((field) =>
          Boolean(
            field.type === 'file_upload' ? invalidFiles?.[field.id] : invalidAnswers?.[field.id]
          )
        )
      )
      invalidStep = sectionIndex >= 0 ? sectionIndex + 1 : activeStep
    }
    const invalidAnswers = validationErrors.answers as Record<string, unknown> | undefined
    const invalidFiles = validationErrors.files as Record<string, unknown> | undefined
    setStepValidation(() => {
      const next: Record<number, 'valid' | 'invalid'> = {
        0: validationErrors.applicantInfo ? 'invalid' : 'valid',
      }
      sortedSections.forEach((section, sectionIndex) => {
        next[sectionIndex + 1] = section.fields.some((field) =>
          Boolean(
            field.type === 'file_upload' ? invalidFiles?.[field.id] : invalidAnswers?.[field.id]
          )
        )
          ? 'invalid'
          : 'valid'
      })
      return next
    })
    setActiveStep(invalidStep)
  }

  if (loading) {
    return (
      <div className="container mx-auto max-w-2xl px-6 py-16 space-y-6">
        <Skeleton className="h-10 w-3/4" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (error || !application) {
    return (
      <div className="container mx-auto max-w-2xl px-6 py-16 text-center">
        <p className="text-destructive">
          {error ?? withErrorCode('Application not found.', 'applications/not-found')}
        </p>
      </div>
    )
  }

  if (submitted || paymentConfirmed) {
    return (
      <div className="container mx-auto max-w-2xl px-6 py-20 text-center">
        <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-green-500" />
        <h2 className="text-2xl font-bold">Application Submitted!</h2>
        <p className="mt-2 text-muted-foreground">
          {paymentConfirmed ? 'Thank you — your payment was received.' : 'Thank you for applying.'}{' '}
          We'll be in touch at <strong>{submittedEmail}</strong>.
        </p>
      </div>
    )
  }

  const isDraft = application.status === 'draft'
  const isClosed =
    application.status === 'closed' ||
    (application.deadline
      ? (() => {
          const d: Date =
            (application.deadline as unknown as { toDate(): Date }).toDate?.() ??
            new Date(application.deadline as unknown as string)
          return d < new Date()
        })()
      : false)
  const isBlocked = isClosed || isDraft

  return (
    <div className="container mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-16">
      {/* Header */}
      <div className="mb-6 space-y-2">
        {application.imageUrl && (
          <div className="mb-6 overflow-hidden rounded-xl">
            <img
              src={application.imageUrl}
              alt={application.title}
              className="aspect-video w-full object-cover"
              style={{
                objectPosition: `${(application.imageFocusX ?? 0.5) * 100}% ${(application.imageFocusY ?? 0.5) * 100}%`,
              }}
            />
          </div>
        )}
        <h1 className="text-3xl font-bold">{application.title}</h1>
        <p className="text-muted-foreground">{application.description}</p>
      </div>

      <DeadlineBanner application={application} />

      {isBlocked ? null : (
        <div className="mt-8 lg:grid lg:grid-cols-[16rem_minmax(0,1fr)] lg:items-start lg:gap-10 xl:gap-14">
          <aside className="hidden lg:sticky lg:top-24 lg:block">
            <div className="rounded-2xl border bg-card/70 p-5 shadow-sm backdrop-blur">
              <p className="mb-5 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Application progress
              </p>
              <Stepper
                active={activeStep}
                steps={stepperSteps}
                orientation="vertical"
                onStepChange={(index) => void navigateToStep(index)}
                aria-label="Application progress"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Step {activeStep + 1} of {totalStepCount}
              </p>
            </div>
          </aside>

          <div className="min-w-0">
            <div className="mb-6 overflow-x-auto pb-2 [scrollbar-width:thin] lg:hidden">
              <div
                className="rounded-2xl border bg-card/70 px-4 py-4 shadow-sm"
                style={{ minWidth: `max(100%, ${stepperSteps.length * 10}rem)` }}
              >
                <Stepper
                  ref={horizontalStepperRef}
                  active={activeStep}
                  steps={stepperSteps}
                  onStepChange={(index) => void navigateToStep(index)}
                  aria-label="Application progress"
                />
              </div>
            </div>

            <div ref={stepContentRef} className="scroll-mt-24">
              {paymentStep ? (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-brand-orange">Final step</p>
                    <h2 className="mt-1 text-2xl font-semibold">Complete payment to submit</h2>
                  </div>
                  <ApplicationPaymentStep
                    clientSecret={paymentStep.clientSecret}
                    amountCents={paymentStep.amountCents}
                    currency={paymentStep.currency}
                    onSuccess={() => setPaymentConfirmed(true)}
                  />
                </div>
              ) : (
                <form
                  onSubmit={(event) => void handleSubmit(onSubmit, handleInvalid)(event)}
                  className="space-y-6"
                >
                  {activeStep === 0 ? (
                    <ApplicantInfoSection
                      register={
                        register as unknown as Parameters<
                          typeof ApplicantInfoSection
                        >[0]['register']
                      }
                      errors={errors}
                    />
                  ) : null}

                  {sortedSections.map((section, sectionIndex) => (
                    <div
                      key={section.id}
                      className={activeStep === sectionIndex + 1 ? undefined : 'hidden'}
                    >
                      <DynamicFormRenderer
                        sections={[section]}
                        register={register}
                        control={control}
                        setValue={setValue}
                        errors={errors}
                        onFileUpload={handleFileUpload}
                        onFileRemove={handleFileRemove}
                        onFileStateChange={handleFileStateChange}
                        disabled={submitting || initializingPayment}
                      />
                    </div>
                  ))}

                  {submitError && (
                    <p className="text-sm text-destructive" role="alert" aria-live="polite">
                      {submitError}
                    </p>
                  )}

                  {activeStep === contentStepCount - 1 && (
                    <div className="flex items-start gap-3 rounded-xl border bg-muted/30 p-4">
                      <Checkbox
                        id="application-terms-accepted"
                        checked={termsAccepted}
                        onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                        aria-required="true"
                      />
                      <label
                        htmlFor="application-terms-accepted"
                        className="cursor-pointer text-sm leading-5"
                      >
                        I agree to the{' '}
                        <Link
                          to="/terms"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline underline-offset-4"
                        >
                          Terms of Service
                        </Link>{' '}
                        and acknowledge the{' '}
                        <Link
                          to="/privacy"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline underline-offset-4"
                        >
                          Privacy Policy
                        </Link>
                        .
                      </label>
                    </div>
                  )}

                  <div className="flex flex-col-reverse gap-3 border-t pt-5 sm:flex-row sm:items-center sm:justify-between">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => void navigateToStep(Math.max(0, activeStep - 1))}
                      disabled={activeStep === 0 || submitting || initializingPayment}
                    >
                      <ChevronLeft />
                      Back
                    </Button>

                    {activeStep < contentStepCount - 1 ? (
                      <Button type="button" onClick={() => void goToNextStep()}>
                        Continue
                        <ChevronRight />
                      </Button>
                    ) : (
                      <Button
                        type="submit"
                        className="min-w-48"
                        disabled={
                          submitting || initializingPayment || uploadsInProgress || !termsAccepted
                        }
                      >
                        {submitting || initializingPayment ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {initializingPayment ? 'Preparing payment…' : 'Submitting…'}
                          </>
                        ) : paymentField ? (
                          'Continue to Payment'
                        ) : (
                          'Submit Application'
                        )}
                      </Button>
                    )}
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
